import i18n from 'src/i18n';
import { LEAD_MOVED_EVENT } from 'src/pages/Pipe/Pipe.constants';
import { RouterPaths } from '../Router/Router.constants';
import { queryVisibleTarget } from '../Tour/Tour.functions';
import type { tTourAdvanceControls, tTourStep } from '../Tour/Tour.types';
import { CHALLENGE_CENTER_NS } from './ChallengeCenter.constants';
import type { tChallengeKey } from './ChallengeCenter.types';

/**
 * One guided tour per challenge that has a real, reachable place in the
 * product to point at (SAD D14: the tour ends one step before the
 * irreversible action — it never submits on the person's behalf). Each tour
 * escorts as deep as the product allows: it opens the tab, the menu or the
 * modal the mission lives in (via `onBeforeShow`) and points at the real
 * control inside. Every step with a control also advances when the person
 * uses it (`advanceOn`), so a tour can be walked by doing instead of by
 * pressing "Siguiente"; the last control ends the tour. `attend_leads` is a
 * habit, not a control, and has no entry.
 *
 * `requiredPath` gates the trigger button: the panel only offers "Muéstrame
 * cómo" when the person is already on that route (mirrors the pre-existing
 * `move_lead` behavior), otherwise it offers a "Ve a ..." navigation link
 * whose label is `routeLabelKey`, resolved with `t()` in the panel.
 */
export type tChallengeTourConfig = {
  requiredPath: string;
  routeLabelKey: string;
  steps: tTourStep[];
  // Reverses whatever the tour's `onBeforeShow` hooks opened (a modal, a
  // menu, a picker), so "Saltar", "Listo", Escape or an early end never
  // leave the person inside something they did not open. Idempotent: it
  // runs on every way out and must be safe when nothing is open.
  onEnd?: () => void;
};

// Dispatches a real click so an SVG icon (heroicons render <svg>, which has
// no .click()) or its owning button receives it; React's root listener
// sees a bubbling MouseEvent either way. It clicks the visible copy, like
// `target`: the step before may have opened any stage column's menu.
function click(selector: string): void {
  const el = queryVisibleTarget<Element>(selector);
  if (!el) return;
  const target = el instanceof HTMLElement ? el : el.closest<HTMLElement>('button, a, [role="button"]') ?? el;
  target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
}

// The stage menu is a focus-driven dropdown (react-daisyui without `open`):
// focusing its toggle is what opens it, a click would not. The visible
// toggle: a stage in selection mode hides its header, toggle included, and
// a hidden toggle takes no focus, so its menu would never open.
function focus(selector: string): void {
  queryVisibleTarget(selector)?.focus();
}

// Repeated components render one copy per instance (a menu per stage
// column), so a step resolves the copy the person can see (GRO-1247).
function target(selector: string): () => HTMLElement | null {
  return () => queryVisibleTarget(selector);
}

// react-select renders its open menu inside the control's wrapper with an
// emotion class ending in `-menu`. Not a data-tour hook (react-select owns
// the class), so it lives here as a template literal on purpose: the registry
// test maps every single-quoted selector literal to a source file.
const REACT_SELECT_MENU = `[class$="-menu"]`;
function reactSelectMenuOf(wrapperSelector: string): () => HTMLElement | null {
  return () =>
    document.querySelector<HTMLElement>(wrapperSelector)?.querySelector<HTMLElement>(REACT_SELECT_MENU) ?? null;
}
// Picking an option is what moves a react-select step on: it fires no
// native `change`, and the option rows carry ARIA roles, not data-tour.
const REACT_SELECT_OPTION = `[role="option"]`;
function nextOnReactSelectPick(wrapperSelector: string): tTourStep['advanceOn'] {
  return nextOn(`${wrapperSelector} ${REACT_SELECT_OPTION}`);
}

// Advance-on-action (see tTourStep.advanceOn): the step moves on when the
// person performs its action instead of pressing "Siguiente". Listens in
// the capture phase on `document` so an SVG child, a label or a portal all
// count, and matches the event's origin against the step's selector.
// `click` by default; text fields use `change` (commits on blur, so the
// spotlight does not jump on the first keystroke).
function listenFor(
  selector: string,
  events: string[],
  pick: (controls: tTourAdvanceControls) => () => void,
): tTourStep['advanceOn'] {
  return (controls) => {
    const go = pick(controls);
    const handler = (event: Event) => {
      const origin = event.target as Element | null;
      if (origin?.closest?.(selector)) go();
    };
    events.forEach((name) => document.addEventListener(name, handler, true));
    return () => events.forEach((name) => document.removeEventListener(name, handler, true));
  };
}
function nextOn(selector: string, events: string[] = ['click']): tTourStep['advanceOn'] {
  return listenFor(selector, events, (c) => c.next);
}
function finishOn(selector: string, events: string[] = ['click']): tTourStep['advanceOn'] {
  return listenFor(selector, events, (c) => c.finish);
}
// A page-level announcement (the funnel fires LEAD_MOVED_EVENT once the API
// confirms a drop) ends the tour: the mission itself just happened.
function finishOnWindowEvent(name: string): tTourStep['advanceOn'] {
  return ({ finish }) => {
    window.addEventListener(name, finish);
    return () => window.removeEventListener(name, finish);
  };
}

// Focus-driven dropdowns (the stage menu) close when focus leaves them;
// click-outside dropdowns (the chat plus menu) close on a document mousedown.
function closeOpenMenus(): void {
  (document.activeElement as HTMLElement | null)?.blur?.();
  document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
}

// The first button with that exact label inside the open modal, if any.
function clickModalButton(label: string): void {
  const modal = document.querySelector<HTMLElement>('.modal-open');
  if (!modal) return;
  const button = [...modal.querySelectorAll<HTMLButtonElement>('button')].find(
    (b) => b.textContent?.trim() === label,
  );
  button?.click();
}

const LEAD_CARD = '[data-testid^="lead-card-"]';

// The whole column a stage title heads: its highest ancestor that still holds
// only that one title (the stage root wraps the title and its cards).
function stageColumnOf(title: HTMLElement): HTMLElement {
  let column = title;
  while (column.parentElement && column.parentElement.querySelectorAll('.Stage__Title').length === 1) {
    column = column.parentElement;
  }
  return column;
}

// The column the pointed-at lead would be dragged into: the first stage that
// is not the card's own. With the first column empty the first card sits in
// the second one, and pointing there asked for a same-column drop, which
// moves nothing (AGI-498). A one-column funnel still has a card to point at,
// just nowhere to move it.
function nextStageTitle(): HTMLElement | null {
  const stageTitles = [...document.querySelectorAll<HTMLElement>('.Stage__Title')];
  if (stageTitles.length <= 1) return stageTitles[0] ?? null;
  const card = document.querySelector<HTMLElement>(LEAD_CARD);
  const destination = card ? stageTitles.find((title) => !stageColumnOf(title).contains(card)) : undefined;
  return destination ?? stageTitles[1];
}

// Function-form title/body (see tTourStep): copy resolves at Tour render, so
// a language change mid-tour, or a namespace that finishes loading after the
// registry module was evaluated, both show the right words.
function copy(tourKey: tChallengeKey, stepId: string): Pick<tTourStep, 'id' | 'title' | 'body'> {
  return {
    id: stepId,
    title: () => i18n.t(`tours.${tourKey}.${stepId}.title`, { ns: CHALLENGE_CENTER_NS }),
    body: () => i18n.t(`tours.${tourKey}.${stepId}.body`, { ns: CHALLENGE_CENTER_NS }),
  };
}

export const CHALLENGE_TOURS: Partial<Record<tChallengeKey, tChallengeTourConfig>> = {
  connect_whatsapp: {
    requiredPath: RouterPaths.dashboard,
    routeLabelKey: 'routes.dashboard',
    steps: [
      {
        ...copy('connect_whatsapp', 'connect-whatsapp'),
        placement: 'bottom',
        gesture: 'tap',
        getTarget: target('[data-tour="connect-whatsapp-api-button"]'),
        advanceOn: finishOn('[data-tour="connect-whatsapp-api-button"]'),
      },
    ],
  },
  invite_team: {
    requiredPath: RouterPaths.settings,
    routeLabelKey: 'routes.settings',
    // The tour opened "Agrega nuevo usuario"; its own close control shuts it
    // and resets the form, which is exactly what leaving should do.
    // react-daisyui puts the modal's className on the box, not the dialog,
    // so the close icon is the stable handle; the click bubbles to its button.
    onEnd: () => click('.TeamSection__Modal__CloseIcon'),
    steps: [
      {
        ...copy('invite_team', 'settings-team-tab'),
        placement: 'bottom',
        gesture: 'tap',
        onBeforeShow: () => click('[data-tour="settings-team-tab"]'),
        getTarget: target('[data-tour="settings-team-tab"]'),
        advanceOn: nextOn('[data-tour="settings-team-tab"]'),
      },
      {
        ...copy('invite_team', 'invite-team'),
        placement: 'bottom',
        gesture: 'tap',
        getTarget: target('[data-tour="invite-team-member-button"]'),
        advanceOn: nextOn('[data-tour="invite-team-member-button"]'),
      },
      // Inside the modal: the form is the mission. Opening it here is the
      // same click the person was just shown; the tour still stops before
      // "Agregar", which is the irreversible part.
      {
        ...copy('invite_team', 'invite-name'),
        placement: 'right',
        gesture: 'tap',
        onBeforeShow: () => click('[data-tour="invite-team-member-button"]'),
        getTarget: target('[data-tour="invite-name-input"]'),
        advanceOn: nextOn('[data-tour="invite-name-input"]', ['change']),
      },
      {
        ...copy('invite_team', 'invite-email'),
        placement: 'right',
        gesture: 'tap',
        getTarget: target('[data-tour="invite-email-input"]'),
        advanceOn: nextOn('[data-tour="invite-email-input"]', ['change']),
      },
      {
        ...copy('invite_team', 'invite-role'),
        placement: 'right',
        gesture: 'tap',
        getTarget: target('[data-tour="invite-role-select"]'),
        // react-select: the open menu is absolutely positioned, so the
        // spotlight widens to it, and picking an option (no native change
        // event) is what moves on.
        expandTo: reactSelectMenuOf('[data-tour="invite-role-select"]'),
        advanceOn: nextOnReactSelectPick('[data-tour="invite-role-select"]'),
      },
      {
        ...copy('invite_team', 'invite-submit'),
        placement: 'top',
        gesture: 'tap',
        getTarget: target('[data-tour="invite-submit-button"]'),
        // Their click, their invite: the tour ends and the mission completes
        // through user.invited like any other send.
        advanceOn: finishOn('[data-tour="invite-submit-button"]'),
      },
    ],
  },
  review_stages: {
    requiredPath: RouterPaths.funnels,
    routeLabelKey: 'routes.funnels',
    onEnd: () => {
      clickModalButton(i18n.t('stage.createStageModal.cancel', { ns: 'pipe/funnel' }));
      closeOpenMenus();
    },
    steps: [
      {
        ...copy('review_stages', 'stage-menu'),
        placement: 'bottom',
        gesture: 'tap',
        getTarget: target('[data-tour="stage-menu-toggle"]'),
        advanceOn: nextOn('[data-tour="stage-menu-toggle"]'),
      },
      {
        ...copy('review_stages', 'stage-configure'),
        placement: 'right',
        gesture: 'tap',
        onBeforeShow: () => focus('[data-tour="stage-menu-toggle"]'),
        getTarget: target('[data-tour="stage-menu-configure"]'),
        advanceOn: nextOn('[data-tour="stage-menu-configure"]'),
      },
      // Inside the editor. Opening it is the mission itself (it reports the
      // UI signal), so from here the tour is teaching what the editor holds;
      // it never presses "Guardar". The name field only exists in the
      // Lead Agent variant of the editor, so that step auto-skips elsewhere.
      {
        ...copy('review_stages', 'stage-editor-name'),
        placement: 'right',
        gesture: 'tap',
        onBeforeShow: () => click('[data-tour="stage-menu-configure"]'),
        getTarget: target('[data-tour="stage-editor-name"]'),
        advanceOn: nextOn('[data-tour="stage-editor-name"]', ['change']),
      },
      {
        ...copy('review_stages', 'stage-editor-description'),
        placement: 'right',
        gesture: 'tap',
        onBeforeShow: () => click('[data-tour="stage-menu-configure"]'),
        getTarget: target('[data-tour="stage-editor-description"]'),
        advanceOn: nextOn('[data-tour="stage-editor-description"]', ['change']),
      },
      {
        ...copy('review_stages', 'stage-editor-owner'),
        placement: 'right',
        gesture: 'tap',
        getTarget: target('[data-tour="stage-editor-owner"]'),
        advanceOn: nextOn('[data-tour="stage-editor-owner"]', ['click', 'change']),
      },
      {
        ...copy('review_stages', 'stage-editor-save'),
        placement: 'top',
        gesture: 'tap',
        getTarget: target('[data-tour="stage-editor-save"]'),
        // Guardar is the mission (a saved stage change): their click ends the
        // tour and completes it for real.
        advanceOn: finishOn('[data-tour="stage-editor-save"]'),
      },
    ],
  },
  respond_lead: {
    requiredPath: RouterPaths.chat,
    routeLabelKey: 'routes.chat',
    onEnd: () => {
      click('.WabTemplateActions__Header__Icon');
      closeOpenMenus();
    },
    steps: [
      {
        ...copy('respond_lead', 'lead-row'),
        placement: 'right',
        gesture: 'tap',
        getTarget: target('[data-testid="messenger-lead-row"]'),
        advanceOn: nextOn('[data-testid="messenger-lead-row"]'),
      },
      {
        ...copy('respond_lead', 'reply-box'),
        placement: 'top',
        gesture: 'tap',
        // The reply box only exists with a conversation open: open the first
        // one, the same tap the person was just shown.
        onBeforeShow: () => click('[data-testid="messenger-lead-row"]'),
        getTarget: target('.TextAreaContainer__TextArea'),
        advanceOn: nextOn('.TextAreaContainer__TextArea', ['change']),
      },
      {
        ...copy('respond_lead', 'send-button'),
        placement: 'top',
        gesture: 'tap',
        getTarget: target('[data-tour="chat-send-button"]'),
        advanceOn: nextOn('[data-tour="chat-send-button"]'),
      },
      {
        ...copy('respond_lead', 'plus-menu'),
        placement: 'top',
        gesture: 'tap',
        getTarget: target('[data-tour="chat-plus-menu"]'),
        advanceOn: nextOn('[data-tour="chat-plus-menu"]'),
      },
      // The template path, for a conversation past its 24-hour window. The
      // item only renders on a WhatsApp API chat; elsewhere these two steps
      // auto-skip and the tour ends on the plus menu.
      {
        ...copy('respond_lead', 'plus-send-template'),
        placement: 'top',
        gesture: 'tap',
        onBeforeShow: () => click('[data-tour="chat-plus-menu"]'),
        getTarget: target('[data-tour="chat-plus-send-template"]'),
        advanceOn: nextOn('[data-tour="chat-plus-send-template"]'),
      },
      {
        ...copy('respond_lead', 'template-send'),
        placement: 'top',
        gesture: 'tap',
        onBeforeShow: () => click('[data-tour="chat-plus-send-template"]'),
        // The picker fetches its templates before the send control mounts
        // (~450ms measured in staging); the last step's default poll is 150ms.
        targetTimeoutMs: 1500,
        getTarget: target('[data-tour="wab-template-send"]'),
        advanceOn: finishOn('[data-tour="wab-template-send"]'),
      },
    ],
  },
  move_lead: {
    requiredPath: RouterPaths.funnels,
    routeLabelKey: 'routes.funnels',
    steps: [
      {
        ...copy('move_lead', 'move-lead-target'),
        placement: 'bottom',
        getTarget: target(LEAD_CARD),
        // The only drag in the catalog: the connector shows where the card
        // goes, the ghost shows it going (DES-215).
        guideTo: nextStageTitle,
        gesture: 'drag',
        // A real drop is the mission: the funnel announces it and the tour
        // ends there instead of pointing at a destination already reached.
        advanceOn: finishOnWindowEvent(LEAD_MOVED_EVENT),
      },
      {
        ...copy('move_lead', 'move-lead-destination'),
        placement: 'left',
        // A destination, not a control: nobody taps a column to drop a card,
        // so no gesture — the spotlight alone says "here".
        getTarget: nextStageTitle,
        advanceOn: finishOnWindowEvent(LEAD_MOVED_EVENT),
      },
    ],
  },
  send_bulk_template: {
    requiredPath: RouterPaths.funnels,
    routeLabelKey: 'routes.funnels',
    onEnd: () => {
      click('.BulkMessageWABModal__Header__CloseIcon');
      closeOpenMenus();
    },
    steps: [
      {
        ...copy('send_bulk_template', 'stage-menu'),
        placement: 'bottom',
        gesture: 'tap',
        getTarget: target('[data-tour="stage-menu-toggle"]'),
        advanceOn: nextOn('[data-tour="stage-menu-toggle"]'),
      },
      {
        ...copy('send_bulk_template', 'stage-bulk-message'),
        placement: 'right',
        gesture: 'tap',
        onBeforeShow: () => focus('[data-tour="stage-menu-toggle"]'),
        getTarget: target('[data-tour="stage-menu-bulk-message"]'),
        advanceOn: nextOn('[data-tour="stage-menu-bulk-message"]'),
      },
      {
        ...copy('send_bulk_template', 'bulk-audience'),
        placement: 'top',
        gesture: 'tap',
        onBeforeShow: () => click('[data-tour="stage-menu-bulk-message"]'),
        getTarget: target('[data-tour="bulk-message-next"]'),
        advanceOn: nextOn('[data-tour="bulk-message-next"]'),
      },
      {
        ...copy('send_bulk_template', 'bulk-template'),
        placement: 'bottom',
        gesture: 'tap',
        // "Siguiente" only moves on with an audience; with none, the picker
        // never appears and the tour ends on the step before (auto-skip).
        onBeforeShow: () => click('[data-tour="bulk-message-next"]'),
        getTarget: target('.SelectTemplate__Left__Select'),
      },
      {
        ...copy('send_bulk_template', 'bulk-send'),
        placement: 'top',
        // The last control before the irreversible send: pointed at, never
        // pressed by the tour. Pressed by the person, it ends the tour.
        getTarget: target('[data-tour="bulk-message-next"]'),
        advanceOn: finishOn('[data-tour="bulk-message-next"]'),
      },
    ],
  },
  verify_portfolio: {
    requiredPath: RouterPaths.wapi,
    routeLabelKey: 'routes.wapi',
    steps: [
      {
        ...copy('verify_portfolio', 'wapi-general-tab'),
        placement: 'bottom',
        gesture: 'tap',
        onBeforeShow: () => click('[data-tour="wapi-general-tab"]'),
        getTarget: target('[data-tour="wapi-general-tab"]'),
        advanceOn: nextOn('[data-tour="wapi-general-tab"]'),
      },
      {
        ...copy('verify_portfolio', 'business-verification'),
        placement: 'bottom',
        // A status to read, not a control to press: no gesture.
        getTarget: target('[data-tour="wapi-business-verification"]'),
      },
    ],
  },
};
